import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import config from "../../config/config"; // 路徑依實際專案結構調整
import useStompClient from "../../hook/useStompClient";
import { LockIcon } from "@chakra-ui/icons";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  HStack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Button,
  useBreakpointValue,
  Container,
  VStack,
} from "@chakra-ui/react";
import ClickableImage from "../../components/ClickableImage";
import ChatInput from "../../components/chatInput";
import BlobAvatar from "./avatar";
import ChatShell from "./ChatShell"; // 調整路徑
function Room() {
  const userIdRef = useRef(generateRandomString());
  const userId = userIdRef.current;
  const [getConfig] = useState(config);
  const data = useRef({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [text, setText] = useState("");
  const [talkTo, setTalkTo] = useState("");
  const [renderTrigger, setRenderTrigger] = useState(0); // 用於觸發重新渲染
  const [messageHistory, setMessageHistory] = useState([]);
  const ref = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const params = new URLSearchParams(location.search);
  const name = params.get("name");
  const roomId = params.get("roomId");
  const roomName = params.get("roomName");
  const subsRef = useRef([]);
  const convIdRef = useRef(uuidv4());
  const convId = convIdRef.current;
  const aiMsgIndexRef = useRef(null);
  const streamStateRef = useRef(new Map());
  const bottomRef = useRef(null);
  const GRACE_MS = 120; // done 等待時間（80~150ms 看情況調）

  const serverUrl =
    getConfig.ENV === "TUNNEL"
      ? `${getConfig.SERVER_POINT}/stomp?userId=${userId}`
      : `${getConfig.SERVER_POINT}:${getConfig.API_BASE_PORT}/stomp?userId=${userId}`;
  function generateRandomString() {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 10; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  }

  // 取得人名（安全）
  const uName = (uid, dataRef, fallback = "Unknown") =>
    (uid && dataRef.current[uid]?.name) || fallback;

  // 判斷並回傳樣式與標籤
  const classifyMsg = (msg, meId) => {
    const isSystem = !!msg.system;
    if (isSystem) return { kind: "system" };

    const isBroadcast = !msg.toUserId; // 沒有收件人 = 公開
    const isMine = msg.userId === meId; // 我發的
    const isToMe = msg.toUserId === meId; // 別人私我

    if (isBroadcast) return { kind: isMine ? "minePublic" : "otherPublic" };
    if (isToMe) return { kind: "toMePrivate" };
    if (isMine) return { kind: "myPrivate" };

    // 其他私訊（別人彼此之間），通常不會送到你這，但先保險
    return { kind: "otherPrivate" };
  };

  // 不同類型對應的樣式
  const kindStyle = {
    minePublic: {
      bg: "blue.100",
      border: "blue.200",
      tag: { text: "公開", colorScheme: "blue" },
    },
    otherPublic: {
      bg: "white",
      border: "gray.200",
      tag: { text: "公開", colorScheme: "blue" },
    },
    myPrivate: {
      bg: "purple.100",
      border: "purple.200",
      tag: { text: "私訊", colorScheme: "purple" },
    },
    toMePrivate: {
      bg: "purple.50",
      border: "purple.200",
      tag: { text: "私訊", colorScheme: "purple" },
    },
    otherPrivate: {
      bg: "gray.50",
      border: "gray.200",
      tag: { text: "私訊", colorScheme: "purple" },
    },
  };
  const isImageLike = (m) => {
  // 你如果是放在 msg.image，就改用 m.image 判斷
    const s = m.image ?? m.text ?? "";
    // 常見三種情況：
    // 1) data:image/png;base64,xxxx
    // 2) data:image/jpeg;base64,xxxx
    // 3) 純 base64（不含 data: 前綴）
    return typeof s === "string" && (
      s.startsWith("data:image/") ||
      /^[A-Za-z0-9+/]+={0,2}$/.test(s)  // 簡單偵測 base64 字元
    );
  };

  // 判斷 payload 是否像是圖片（data URI 或長字串 base64）
  const isImagePayload = (s) => {
    if (typeof s !== "string") return false;
    if (s.startsWith("data:image/")) return true;
    // 粗略判斷 base64（避免把一般文字誤判），並要求長度夠長
    return /^[A-Za-z0-9+/=\s]+$/.test(s) && s.length > 200;
  };

  // 轉成可用於 <img> 的 data URI（若已是 data:image/... 會原樣返回）
  const toDataUri = (s) => {
    if (!s) return "";
    return s.startsWith("data:image/") ? s : `data:image/png;base64,${s}`;
  };

  // 統一把後端 { userId, message } 轉成前端訊息物件
  const normalizeInboundMessage = (raw, toUserId = null) => {
    const { userId, message } = raw;
    if (isImagePayload(message)) {
      return { userId, image: toDataUri(message), toUserId };
    }
    return { userId, text: message ?? "", toUserId };
  };



  const handleMessage = (message) => {
    const d = JSON.parse(message.body); // { userId, message }
    const msg = normalizeInboundMessage(d, null);
    setMessageHistory((prev) => [...prev, msg]);
    // setMessageHistory((prev) => [
    //   ...prev,
    //   { userId: d.userId, text: d.message, toUserId: null },
    // ]);

  };
    useEffect(() => {
    if (bottomRef.current) {
      setTimeout(() => {
        bottomRef.current.scrollIntoView({ behavior: "smooth" }); 
      }, 800);
      
      // 或 "auto" 如果不想要滑動動畫
    }
  }, [messageHistory.length]);
  //私訊給我
  const handlePrivateMessage = (message) => {
    const d = JSON.parse(message.body); // 後端: { userId, message }
    const msg = normalizeInboundMessage(d, userId); // 私訊 → toUserId = 自己
    setMessageHistory((prev) => [...prev, msg]);
  };

  const handleDisconnect = (message) => {
    const uid = message.body; // userId
    const who = data.current[uid]?.name ?? uid;
    setMessageHistory((prev) => [
      ...prev,
      { system: true, text: `${who}已離開房間` },
    ]);
    delete data.current[uid];
    setRenderTrigger((prev) => prev + 1);
  };

  const queueNewUser = (message) => {
    const resdata = JSON.parse(message.body); // { [userId]: {name, ...}, ... }
    data.current = resdata;
    setRenderTrigger((prev) => prev + 1);
    setMessageHistory((prev) => [
      ...prev,
      { system: true, text: `歡迎${data.current[userId].name}加入房間` },
    ]);
  };
  const topicNewUser = (message) => {
    const resdata = JSON.parse(message.body);
    data.current = Object.assign({}, data.current, resdata);
    const joinedUser = Object.values(resdata)[0];
    if (joinedUser.name === data.current[userId].name) return;
    setMessageHistory((prev) => [
      ...prev,
      { system: true, text: `歡迎${joinedUser.name}加入房間` },
    ]);
  };

  // convId -> 狀態：下一個要顯示的序號、暫存片段、done 計時器、該回合的訊息 index
  // convId -> 狀態：下一個要顯示的序號、暫存片段、done 計時器、該回合的訊息 index

  const handleAiStream = (message) => {
    let p;
    try {
      p = JSON.parse(message.body);
    } catch {
      p = { type: "token", text: message.body };
    }

    const convId = p.convId || "default";
    let state = streamStateRef.current.get(convId);
    if (!state) {
      state = { next: 0, buf: {}, timer: undefined, msgIndex: null };
      streamStateRef.current.set(convId, state);
    }

    const flushContiguous = () => {
      if (state.msgIndex === null) {
        // 建立/續寫用的 AI 泡泡
        setMessageHistory((prev) => {
          const copy = [...prev];
          state.msgIndex = copy.length;
          copy.push({ userId: "aiUser", text: "", toUserId: p.userId });
          return copy;
        });
      }
      // 把連續可顯示的 i 依序刷到 UI
      setMessageHistory((prev) => {
        const copy = [...prev];
        const idx = state.msgIndex;
        while (state.buf[state.next] !== undefined) {
          copy[idx] = {
            ...copy[idx],
            text: (copy[idx].text || "") + state.buf[state.next],
          };
          delete state.buf[state.next];
          state.next++;
        }
        return copy;
      });
    };

    if (p.type === "token") {
      // 有 token 到→ 取消任何 pending 的 done
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = undefined;
      }
      if (typeof p.i === "number") {
        state.buf[p.i] = (state.buf[p.i] || "") + (p.text || "");
      } else {
        // 沒帶 i 的 fallback：直接追加
        state.buf[state.next] = (state.buf[state.next] || "") + (p.text || "");
      }
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: "smooth" }); 
        // 或 "auto" 如果不想要滑動動畫
      }
      flushContiguous();
    } else if (p.type === "final") {
      // 有完整文本就一次覆蓋當前泡泡
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = undefined;
      }
      setMessageHistory((prev) => {
        const copy = [...prev];
        if (state.msgIndex === null) {
          state.msgIndex = copy.length;
          copy.push({
            userId: "aiUser",
            text: p.text || "",
            toUserId: p.userId,
          });
        } else {
          const idx = state.msgIndex;
          copy[idx] = { ...copy[idx], text: p.text || copy[idx].text };
        }
        return copy;
      });
      // 結束並清理
      streamStateRef.current.delete(convId);
    } else if (p.type === "done") {
      // 不要立刻收；給一個緩衝期，若期間再有 token 到就續寫同一泡泡
      if (state.timer) clearTimeout(state.timer);
      state.timer = setTimeout(() => {
        // 緩衝期後把殘留片段刷完
        flushContiguous();
        // 結束並清理
        streamStateRef.current.delete(convId);
      
      }, GRACE_MS);
    }
  };
  
  const handlers = {
    [`/topic/room.${roomId}/message`]: handleMessage,
    [`/topic/room.${roomId}/disconnect`]: handleDisconnect,
    "/user/queue/newUser": queueNewUser,
    [`/topic/${roomId}/newUser`]: topicNewUser,
    "/user/queue/private": handlePrivateMessage,
    "/user/queue/stream": handleAiStream,
  };
  const { subscribe, publish } = useStompClient({
    url: serverUrl,
    onDisconnect: () => {
      console.log("已從 STOMP 斷開連接");
      navigate("/");
    },
    onStompError: (frame) => {
      console.error("STOMP 錯誤: " + frame.headers["message"]);
      console.error("詳細內容: " + frame.body);
    },
    onConnect: (client) => {
      console.log("已連接 STOMP");
      // Object.values(subRef.current).forEach(s => s?.unsubscribe());
      subsRef.current.forEach((sub) => sub.unsubscribe());
      subsRef.current = [];
      subsRef.current = Object.entries(handlers).map(([topic, handler]) =>
        subscribe(topic, handler)
      );
      // 發送加入聊天室訊息
      publish(
        "/app/chat.join", // 根據你的服務器端點
        { name: name, roomId: roomId }
      );
    },
  });
  const handExit = (e) => {
    navigate("/");
  };
  return (
    <ChatShell
      roomName={roomName}
      headerActions={
        <>
          {isMobile && (
            <Button
              size="sm"
              colorScheme="purple"
              onClick={() => setIsDrawerOpen(true)}
            >
              人員列表
            </Button>
          )}
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => setTalkTo("")}
          >
            清除對象
          </Button>
       
          <Button size="sm" colorScheme="red" onClick={handExit}>
            退出
          </Button>
        </>
      }
      renderMessages={() =>
        messageHistory.map((msg, idx) => {
          const cls = classifyMsg(msg, userId);
          if (cls.kind === "system") {
            return (
              <Box key={idx} textAlign="center" my={3}>
                <Text fontSize="xs" color="gray.500">
                  {msg.text}
                </Text>
              </Box>
            );
          }
          
          const mine = cls.kind === "minePublic" || cls.kind === "myPrivate";
          const style = kindStyle[cls.kind];
          const senderName = uName(msg.userId, data);
          const toName = msg.toUserId
            ? uName(msg.toUserId, data, "你")
            : "大家";
          const header = mine
            ? msg.toUserId
              ? `你 → ${toName}`
              : `你`
            : msg.toUserId
            ? `${senderName} → 你`
            : senderName;

          return (
            <HStack
              key={idx}
              justify={mine ? "flex-end" : "flex-start"}
              align="center"
              mb={3}
              spacing={2}
            >
              {!mine && (
                <BlobAvatar
                  person={data.current[msg.userId]}
                  size="sm"
                  alignSelf="center"
                />
              )}
              <Box
                maxW="70%"
                px={3}
                py={2}
                borderRadius="lg"
                bg={style.bg}
                border="1px solid"
                borderColor={style.border}
                boxShadow="xs"
              >
                <HStack spacing={2} mb={1}>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                    {header}
                  </Text>
                  <HStack spacing={1}>
                    {style.tag.text === "私訊" && (
                      <LockIcon boxSize="10px" color="purple.500" />
                    )}
                    <Box
                      as="span"
                      px="6px"
                      py="1px"
                      fontSize="10px"
                      fontWeight="700"
                      borderRadius="full"
                      bg={`${style.tag.colorScheme}.100`}
                      color={`${style.tag.colorScheme}.700`}
                    >
                      {style.tag.text}
                    </Box>
                  </HStack>
                </HStack>
               {msg.image ? (
                  <ClickableImage dataUriOrBase64={msg.image} alt={`${senderName} 的圖片`} />
                  ) : (
                    <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
                      {msg.text}
                    </Text>
                  )}
              </Box>
              {mine && (
                <Box
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg={cls.kind === "myPrivate" ? "purple.300" : "blue.300"}
                />
              )}
              <div ref={bottomRef} />
            </HStack>
            
          );
        })
      }
      renderInput={() => (
        <HStack spacing={2} align="center">
          <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
            對 {talkTo === "" ? "大家" : data.current[talkTo]?.name} 說：
          </Text>
          <Box flex="1">
            <ChatInput
            
              roomId={roomId}
              talkTo={talkTo}
              userId={userId}
              data={data}
              publish={publish}
              setMessageHistory={setMessageHistory}
              convId={convId}
              aiMsgIndexRef={aiMsgIndexRef}
            />
          </Box>
        
        </HStack>
      )}
      rightSidebar={
        <>
          {/* 📱 手機 Drawer */}
          <Drawer
            isOpen={isDrawerOpen}
            placement="left"
            onClose={() => setIsDrawerOpen(false)}
          >
            <DrawerOverlay />
            <DrawerContent>
              <DrawerHeader borderBottomWidth="1px">人員列表</DrawerHeader>
              <DrawerBody>
                {Object.entries(data.current).map(([index, person]) => (
                  <HStack
                    key={index}
                    p={2.5}
                    mb={2}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => {
                      setTalkTo(index);
                      setIsDrawerOpen(false);
                      console.log(`與 ${person.name} 開始聊天`);
                    }}
                    alignItems="center"
                    justifyContent="flex-start"
                  >
                    <BlobAvatar person={person} size="md" />
                    <Text fontSize="sm" fontWeight="600">
                      {person.name}
                    </Text>
                  </HStack>
                ))}
              </DrawerBody>
            </DrawerContent>
          </Drawer>

          {/* 🖥️ 桌面版側欄（md 以上才顯示） */}
          {!isMobile && (
            <VStack
              spacing={0}
              bg="white"
              borderRadius="xl"
              boxShadow="sm"
              overflow="hidden"
              align="stretch"
              h="100%"
            >
              <HStack
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                  成員（{Object.keys(data.current).length}）
                </Text>
              </HStack>
              <Box flex="1" minH={0} overflowY="auto" px={3} py={3}>
                {Object.entries(data.current).map(([index, person]) => {
                  const isActive = talkTo === index;
                  return (
                    <HStack
                      key={index}
                      p={3}
                      mb={2}
                      borderRadius="lg"
                      bg={isActive ? "blue.50" : "white"}
                      border="1px solid"
                      borderColor={isActive ? "blue.200" : "gray.200"}
                      boxShadow="xs"
                      _hover={{ bg: isActive ? "blue.50" : "gray.50" }}
                      cursor="pointer"
                      onClick={() => {
                        if (name !== person.name) setTalkTo(index);
                      }}
                      alignItems="center"
                    >
                      <BlobAvatar person={person} size="md" />
                      <Text
                        fontSize="sm"
                        fontWeight={isActive ? 700 : 600}
                        noOfLines={1}
                        color="gray.800"
                      >
                        {person.name}
                      </Text>
                    </HStack>
                  );
                })}
              </Box>
            </VStack>
          )}
        </>
      }
    />
  );
}
export default Room;
